import * as THREE from 'three';

import AbstractObject from '../misc/object';

import Panel from './panel';
import Marker from './marker';
import Line from './line';
import Arrow from './arrow';
import { HTMLEditableLabel } from './html-label';

import textureImg from '../../assets/textures/steel_2.jpg';
import { callErrorNotification } from '@components/Notification';

import { generatePlanarUVMap, unitTransform } from '@services/helper.service';

import Scene from '@scene';
import { defaultUnits } from '@config';
import { APP_TEXTS } from '@texts';

const texture = new THREE.TextureLoader().load(textureImg);
texture.wrapS = texture.wrapT = THREE.MirroredRepeatWrapping;
texture.repeat.set(0.5, 0.5);


const labelPadding = 30;

/* eslint-disable */
export default class Beam extends AbstractObject {

  static types = {
    L_NON_SYMMETRICAL: 'l-non-symmetrical',
    L_SYMMETRICAL: 'l-symmetrical',
    FILLED_PIPE: 'fill-pipe',
    SQUARE: 'square',
    FLAT: 'flat',
    PIPE: 'pipe',
    H: 'H',
    T: 'T',
    U: 'u',
    Z: 'z',

    SHAPE: 'shape',
    NONE: 'none'
  };

  constructor(options) {
    super(options);

    if (!this.options.type) throw new Error('Type is required');

    if (this.options.type === Beam.types.SHAPE && (!this.options.shape || !this.options.shape.outer || !this.options.shape.outer.length)) {
      callErrorNotification(APP_TEXTS.NOTIFICATION_ERROR_PROFILE_SHAPE.Content, null, 10000);

      console.error(new Error(APP_TEXTS.NOTIFICATION_INCORRECT_BEAM_SHAPE.Content));

      this.options.type = Beam.types.SQUARE;
    }

    if (this.options.type === Beam.types.NONE) {
      const centerLine = new Line({
        from: {
          x: this.position.x - this.options.shift.x, 
          y: this.position.y, // - (this.options.panelDepth / 2 || 0),
          z: this.position.z - this.options.shift.y
        },
        to: {
          x: this.position.x - this.options.shift.x, 
          y: this.position.y - (this.options.height || 100), // - (this.options.panelDepth / 2 || 0),
          z: this.position.z - this.options.shift.y
        },
        disableDepthTest: true,
        dashed: true,
        customMaterial: new THREE.LineDashedMaterial({
          color: 0xBFA75D,
          linewidth: 1, // Due to limitations of the OpenGL Core Profile with the WebGL renderer on most platforms linewidth will always be 1 regardless of the set value.
          scale: 1,
          dashSize: 10,
          gapSize: 5
        })
      });
      this.object.addMesh(centerLine.mesh);

      const centerGeom = new THREE.CircleGeometry(3, 25);
      const centerMaterial = new THREE.MeshBasicMaterial( { color: 0xBFA75D, depthTest: true } );
      const center = new THREE.Mesh( centerGeom, centerMaterial );
      center.rotation.x = Math.PI / 2;
      // center.position.x = this.position.x - this.options.shift.x;
      center.position.y = this.position.y - 1; // - (this.options.panelDepth / 2 || 0);
      // center.position.z = this.position.z - this.options.shift.y;
      this.object.addMesh(center);

      return;
    }

    this.name = this.options.name || 'beam';
    this.width = this.options.width || 10;
    this.height = this.options.height || 20;
    this.depth = this.options.depth || 10;
    this.edgeSize = this.options.edgeSize || 1;
    this.color = this.options.color || 0xffffff;

    this.beam = this.getObject(this.options.type);
    this.object.addMesh(this.beam);

    if (this.options.rotation !== undefined) { // !== safety-barrier
    this.beam.rotation.z = (this.options.isRotateRight ? -this.options.rotation : this.options.rotation) * Math.PI / 180 + Math.PI;
    }
  }

  get size() {
    if (this.options.type === Beam.types.SHAPE) {
      const depth = Math.abs(this.options.shape.outer[0].x - this.options.shape.outer[this.options.shape.outer.length / 2].x);
      const width = Math.abs(this.options.shape.outer[0].y - this.options.shape.outer[this.options.shape.outer.length / 2].y);

      return {
        width,
        height: this.height || 0,
        depth
      }
    } else {
      return {
        width: this.width || 0,
        height: this.height || 0,
        depth: this.depth || 0
      }
    }
  }

  get position() {
    if (this.beam)
      return this.beam.position;
    else if (this.mesh.children[1]) // center circle
      return this.mesh.children[1].position;
    else 
      return { x: 0, y: 0, z: 0};
  }

  setShift() {
    if (this.options.shift) {
      this.position.x += this.options.shift.x * (-1) || 0;
      this.position.z += this.options.shift.y * (-1) || 0;
    }
  }

  createShiftLabels(options) {
    // if (this.beam && (this.beam.position.x !== 0 || this.beam.position.z !== 0)) {
    if (+Number(this.options.shift.x).toFixed(2) !== 0 || +Number(this.options.shift.y).toFixed(2)!== 0) {
      const marker = new Marker({ size: 8 });
      marker.position.x = this.position.x + this.options.shift.x;
      marker.position.y = this.position.y;
      marker.position.z = this.position.z + this.options.shift.y;
      this.object.addObject(marker);

      // TODO: Only for 2D mode
      if (+Number(this.options.shift.x).toFixed(2) !== 0) {
        const xLabel = this.getXLabel(this.options.shift, this.position, options);
        this.object.addMesh(xLabel.object);
        this.htmlLayer.add(xLabel.HTMLObject);
      }

      // TODO: Only for 2D mode
      if (+Number(this.options.shift.y).toFixed(2) !== 0) {
        const yLabel = this.getYLabel(this.options.shift, this.position, options);
        this.object.addMesh(yLabel.object);
        this.htmlLayer.add(yLabel.HTMLObject);
      }
    }
    return {
      object: this.object,
      HTMLLayer: this.htmlLayer
    }
  }

  getXLabel(shift = {}, beamPos = {}, options) {
    shift.x = shift.x || 0;
    shift.y = shift.y || 0;
    var relativePos = {};
    relativePos.x = shift.x + beamPos.x;
    relativePos.y = shift.y + beamPos.z;

    const object = new THREE.Object3D();
    const padding = shift.y >= 0 ? labelPadding : -labelPadding;
    const offset = 3;

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x + shift.x * (-1), y: beamPos.y, z: relativePos.y + padding + (shift.y >= 0 ? offset : -offset) },
      to: { x: relativePos.x + shift.x * (-1), y: beamPos.y, z: (-relativePos.y + shift.y + (shift.y >= 0 ? -offset : offset)) * (-1) }
    }).mesh);

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x, y: beamPos.y, z: relativePos.y + padding + (shift.y >= 0 ? offset : -offset) },
      to: { x: relativePos.x, y: beamPos.y, z: relativePos.y }
    }).mesh);

    let arrow = new Arrow({
      disableDepthTest: true,
      position: new THREE.Vector3(
        relativePos.x, beamPos.y, relativePos.y + padding
        // 0, labelPadding + shift.y * (-1), 0
      ),
      size: shift.x * (-1),
      direction: Arrow.types.direction.X,
      arrowSize: 15
    });

    object.add(arrow.mesh);

    let text = ((!options || !options.is3DMode || options.showLabels) ? `Ex = ` : "") +
              `${unitTransform(+shift.x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const label = new HTMLEditableLabel({
      text,
      value: unitTransform(+shift.x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: shift.y >= 0 ? HTMLEditableLabel.types.verticalAlign.TOP : HTMLEditableLabel.types.verticalAlign.BOTTOM
    });
    label.position.set(
      -relativePos.x + shift.x / 2, 0, relativePos.y + padding
      // shift.x / -2, 0, labelPadding + shift.y
    );
    label.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        beam: {
          shift: {
            x: unitTransform(+value).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(arrow.position.x, arrow.position.y, arrow.position.z);
        startPos.applyEuler(this.rotation);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(arrow.endPosition.x, arrow.endPosition.y, arrow.endPosition.z);
        endPos.applyEuler(this.rotation);
        endPos.applyEuler(options.rotation);
        label.setDirection(startPos, endPos, false);
      }
      let meshPos = this.mesh.position.clone();
      meshPos.applyEuler(this.mesh.rotation);
      label.position.y = meshPos.y - this.size.height / 2; // label position at the base of the beam
      label.position.z = label.position.z + (shift.y >= 0 ? 20 : -20); // add padding
      label.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      label.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      label.align = HTMLEditableLabel.types.align.CENTER;
    }

    return {
      object,
      HTMLObject: label
    }
  }

  getYLabel(shift = {}, beamPos = {}, options) {
    shift.x = shift.x || 0;
    shift.y = shift.y || 0;
    var relativePos = {};
    relativePos.x = shift.x + beamPos.x;
    relativePos.y = shift.y + beamPos.z;

    const object = new THREE.Object3D();
    const padding = shift.x >= 0 ? labelPadding : -labelPadding;
    const offset = 3;

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x + padding + (shift.x >= 0 ? offset : -offset), y: beamPos.y, z: relativePos.y + shift.y * (-1) },
      to: { x: (-relativePos.x + shift.x + (shift.x >= 0 ? -offset : offset)) * (-1), y: beamPos.y, z: relativePos.y + shift.y * (-1) }
    }).mesh);

    object.add(new Line({
      disableDepthTest: true,
      from: { x: relativePos.x + padding + (shift.x >= 0 ? offset : -offset), y: beamPos.y, z: relativePos.y },
      to: { x: relativePos.x, y: beamPos.y, z: relativePos.y }
    }).mesh);

    let arrow = new Arrow({
      disableDepthTest: true,
      position: new THREE.Vector3(
        relativePos.x + padding, beamPos.y, relativePos.y
      ),
      size: shift.y * (-1),
      direction: Arrow.types.direction.Z,
      arrowSize: 15
    });
    object.add(arrow.mesh);

    let text = ((!options || !options.is3DMode || options.showLabels) ? `Ey = ` : "") +
              `${unitTransform(+shift.y).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const label = new HTMLEditableLabel({
      text,
      value: unitTransform(+shift.y).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: shift.x >= 0 ? HTMLEditableLabel.types.align.RIGHT : HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER
    });
    label.position.set(
      // -labelPadding - shift.x, 0, shift.y / 2
      -relativePos.x -padding, 0, relativePos.y - shift.y / 2
    );
    label.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        beam: {
          shift: {
            y: unitTransform(+value).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(arrow.position.x, arrow.position.y, arrow.position.z);
        startPos.applyEuler(this.rotation);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(arrow.endPosition.x, arrow.endPosition.y, arrow.endPosition.z);
        endPos.applyEuler(this.rotation);
        endPos.applyEuler(options.rotation);
        label.setDirection(startPos, endPos, false);
      }
      let meshPos = this.mesh.position.clone();
      meshPos.applyEuler(this.mesh.rotation);
      label.position.y = meshPos.y - this.size.height / 2; // label position at the base of the beam
      label.position.x = label.position.x + (shift.x >= 0 ? -20 : 20); // add padding
      label.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      label.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      label.align = HTMLEditableLabel.types.align.CENTER;
    }

    return {
      object,
      HTMLObject: label
    }
  }

  getObject(type) {

    switch (type) {
      case Beam.types.H: {
        return this.createHType();
      }
      case Beam.types.L_SYMMETRICAL: {
        return this.createLSymmetrical();
      }
      case Beam.types.L_NON_SYMMETRICAL: {
        return this.createLNonSymmetrical();
      }
      case Beam.types.T: {
        return this.createT();
      }
      case Beam.types.SQUARE: {
        return this.createSquare();
      }
      case Beam.types.PIPE: {
        return this.createPipe();
      }
      case Beam.types.FILLED_PIPE: {
        return this.createFilledPipe();
      }
      case Beam.types.FLAT: {
        return this.createFlat();
      }
      case Beam.types.U: {
        return this.createU();
      }
      case Beam.types.Z: {
        return this.createZ();
      }

      case Beam.types.SHAPE: {
        return this.createShape(this.options.shape);
      }
      default: {
        return this.createSquare();
      }
    }
  }

  createShape({ outer, inner }) {
    const shape = new THREE.Shape();

    if (outer) {
      shape.moveTo(outer[0].x, outer[0].y);

      outer.slice(1).forEach(i => {
        shape.lineTo(i.x, i.y);
      });
    }

    if (inner) {
      inner.forEach(list => {
        const hole = new THREE.Path();
        hole.moveTo(list[0].x, list[0].y);

        list.slice(1).forEach(i => {
          hole.lineTo(i.x, i.y);
        });

        shape.holes.push(hole);
      });
    }

    const extrudeSettings = {
      depth: this.height,
      bevelEnabled: false
    };

    var geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry = generatePlanarUVMap(geometry);

    const material = new THREE.MeshLambertMaterial({
      color: this.color || 0xffffff,
      map: texture
    });
      const object = new THREE.Mesh(geometry, material);

    object.rotation.x = Math.PI / 2;
    object.position.y = this.height / 2;

    return object;
  }

  createHType() {
    const object = new THREE.Object3D();

    const leftSide = this.getSide(this.width, this.height, this.edgeSize);
    leftSide.position.z = this.width / 2;
    object.add(leftSide);

    const rightSide = this.getSide(this.width, this.height, this.edgeSize);
    rightSide.position.z = -(this.width / 2);
    object.add(rightSide);

    const middle = this.getSide(this.depth, this.height, this.edgeSize);
    middle.rotation.y = Math.PI / 2;
    object.add(middle);

    return object;
  }

  createLSymmetrical() {
    const object = new THREE.Object3D();

    const leftSide = this.getSide(this.width + this.edgeSize * 2, this.height, this.edgeSize);
    leftSide.position.z = this.width / 2 + this.edgeSize / 2;
    object.add(leftSide);

    const middle = this.getSide(this.depth, this.height, this.edgeSize);
    middle.rotation.y = Math.PI / 2;
    middle.position.x = this.depth / 2 + this.edgeSize / 2;
    object.add(middle);

    return object;
  }

  createLNonSymmetrical() {
    const object = new THREE.Object3D();

    const leftSide = this.getSide(this.width, this.height, this.edgeSize);
    leftSide.position.z = this.width / 2;
    object.add(leftSide);

    const middle = this.getSide(this.depth / 2, this.height, this.edgeSize);
    middle.rotation.y = Math.PI / 2;
    middle.position.x = this.depth / 2 - this.edgeSize / 2;
    middle.position.z = this.depth / 4;
    object.add(middle);

    return object;
  }

  createT() {
    const object = new THREE.Object3D();

    const leftSide = this.getSide(this.width, this.height, this.edgeSize);
    leftSide.position.z = this.width / 2 + this.edgeSize / 2;
    object.add(leftSide);

    const middle = this.getSide(this.depth, this.height, this.edgeSize);
    middle.rotation.y = Math.PI / 2;
    object.add(middle);

    return object;
  }

  createSquare() {
    const object = new THREE.Object3D();

    const eastSide = this.getSide(this.width, this.height, this.edgeSize);
    eastSide.position.z = this.depth / 2;
    object.add(eastSide);

    const westSide = this.getSide(this.width, this.height, this.edgeSize);
    westSide.position.z = this.depth / -2;
    object.add(westSide);

    const northSize = this.getSide(this.depth - this.edgeSize, this.height, this.edgeSize);
    northSize.rotation.y = Math.PI / 2;
    northSize.position.x = this.width / 2 - this.edgeSize / 2;
    object.add(northSize);

    const southSize = this.getSide(this.depth - this.edgeSize, this.height, this.edgeSize);
    southSize.rotation.y = Math.PI / 2;
    southSize.position.x = this.width / -2 + this.edgeSize / 2;
    object.add(southSize);

    return object;
  }

  createFilledPipe() {
    const geometry = new THREE.CylinderGeometry(this.width, this.width, this.height, this.width * 10);

    const material = new THREE.MeshLambertMaterial({
      color: this.color || 0xffffff,
      side: THREE.DoubleSide,
      map: texture
    });

    const mesh = new THREE.Mesh(geometry, material);

    return mesh;
  }

  createPipe() {
    //TODO: Refactoring
    const width = this.width;

    const arcShape = new THREE.Shape();
    arcShape.arc(0, 0, width, 0, Math.PI * 2);

    const holePath = new THREE.Path();
    holePath.arc(0, 0, width - 0.8, 0, Math.PI * 2);

    arcShape.holes.push(holePath);

    var geometry = new THREE.ExtrudeGeometry(arcShape, {
      depth: this.height,
      bevelEnabled: false,
      steps: 1,
      curveSegments: 60
    });
    geometry.center();
    geometry.rotateX(Math.PI * -.5);

    geometry = generatePlanarUVMap(geometry);

    const material = new THREE.MeshLambertMaterial({
      color: this.color || 0xffffff,
      side: THREE.DoubleSide,
      map: texture
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.x = width / 2 * 0.1;
    mesh.scale.z = width / 2 * 0.1;

    return mesh;
  }

  createFlat() {
    const side = this.getSide(this.width, this.height, this.edgeSize);
    side.position.z = this.width / 2 + this.edgeSize / 2;
    return side;
  }

  createU() {
    const object = new THREE.Object3D();

    const eastSide = this.getSide(this.depth, this.height, this.edgeSize);
    eastSide.position.z = this.depth / 2;
    object.add(eastSide);

    const northSize = this.getSide(this.width - this.edgeSize, this.height, this.edgeSize);
    northSize.rotation.y = Math.PI / 2;
    northSize.position.x = this.width / 2 - this.edgeSize / 2;
    object.add(northSize);

    const southSize = this.getSide(this.width - this.edgeSize, this.height, this.edgeSize);
    southSize.rotation.y = Math.PI / 2;
    southSize.position.x = this.width / -2 + this.edgeSize / 2;
    object.add(southSize);

    return object;
  }

  createZ() {
    const object = new THREE.Object3D();

    const firstSide = this.getSide(this.width - this.edgeSize, this.height, this.edgeSize);
    firstSide.position.x = this.depth / 2 - this.edgeSize / 2;
    firstSide.position.z = this.width / 2;

    firstSide.rotation.y = Math.PI / 2;
    object.add(firstSide);

    const middleSide = this.getSide(this.width, this.height, this.edgeSize);
    object.add(middleSide);

    const secondSide = this.getSide(this.width - this.edgeSize, this.height, this.edgeSize);
    secondSide.position.x = -this.depth / 2 + this.edgeSize / 2;
    secondSide.position.z = -this.width / 2;

    secondSide.rotation.y = Math.PI / 2;
    object.add(secondSide);

    return object;
  }

  getSide(width, height, depth) {
    const side = new Panel({
      width,
      height,
      color: this.color,
      depth
    }, texture);

    return side.mesh;
  }

}



// WEBPACK FOOTER //
// ./src/object-view/objects/beam.js